import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Form, useLoaderData, useLocation } from "react-router-dom";

import { yupResolver } from "@hookform/resolvers/yup";

import DeletePlayerModal from "./DeletePlayerModal";
import PlayerButtons from "./PlayerButtons";
import PlayerForm from "./PlayerForm";

import { useRosterContext } from "../contexts/Roster";
import useRosterHandlers from "../hooks/useRosterHandlers";
import playerFormSchema from "../lib/playerFormSchema";

import {
    selectPositions,
    selectSetting,
} from "../../../../../features/global/globalSlice";
import { useAppSelector } from "../../../../../hooks/reduxHooks";
import { canEditTeam, defaultDateFormat } from "../../../../../lib/utils";
import { ITeam } from "../../../../../types/api";
import { IPlayerFormData } from "../../../../../types/forms";

const Player = (): React.JSX.Element => {
    const { player, rosterCount, currentYear, currentTeamsLeague } =
        useRosterContext();

    const location = useLocation();
    const isAddingPlayer = /team\/\d+\/roster\/add\/?$/.test(location.pathname);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const positions = useAppSelector(selectPositions);
    const { handleAddPlayer, handleEditPlayer } = useRosterHandlers();

    const maxRosterSize = parseInt(
        useAppSelector(state =>
            selectSetting(state, "majorybl_max_roster_size"),
        )?.setting,
    );

    const { team: currentTeam } = useLoaderData() as {
        team: ITeam;
    };

    const teamAtMaxSize = isAddingPlayer && maxRosterSize < rosterCount;
    const formHandler = isAddingPlayer ? handleAddPlayer : handleEditPlayer;

    const methods = useForm<IPlayerFormData>({
        mode: "onSubmit",
        resolver: yupResolver(playerFormSchema),
        defaultValues: {
            playerId: player?.id,
            active: isAddingPlayer ? 0 : player?.active,
            dob: player?.dob
                ? defaultDateFormat.format(
                      new Date(`${player.dob}T00:00:00` as string),
                  )
                : undefined,
            email: player?.email,
            firstName: player?.firstName,
            lastName: player?.lastName,
            uniformNumber: player?.uniformNumber,
            isPrimary: isAddingPlayer
                ? true
                : player?.teams.find(t => {
                      if (currentTeamsLeague) {
                          return t.year === currentYear;
                      } else {
                          return (
                              t.year === currentTeam.lastRegistered.year &&
                              t.season === currentTeam.lastRegistered.season
                          );
                      }
                  })?.primary?.id === currentTeam.id,
            positions: player?.positions
                ? player.positions.map(p => {
                      return {
                          value: parseInt(p.toString()),
                          label: positions.find(i => i.id == p)
                              ?.position as string,
                      };
                  })
                : [],
        },
    });

    const isPlayerActive = methods.watch("active");
    const isPrimaryTeam = methods.watch("isPrimary");

    useEffect(() => {
        methods.reset({
            playerId: player?.id,
            active: isAddingPlayer ? 0 : player?.active,
            dob: player?.dob
                ? defaultDateFormat.format(
                      new Date(`${player.dob}T00:00:00` as string),
                  )
                : undefined,
            email: player?.email,
            firstName: player?.firstName,
            lastName: player?.lastName,
            uniformNumber: player?.uniformNumber,
            isPrimary: isAddingPlayer
                ? true
                : player?.teams.find(t => {
                      if (currentTeamsLeague) {
                          return t.year === currentYear;
                      } else {
                          return (
                              t.year === currentTeam.lastRegistered.year &&
                              t.season === currentTeam.lastRegistered.season
                          );
                      }
                  })?.primary?.id === currentTeam.id,
            positions: player?.positions
                ? player.positions.map(p => {
                      return {
                          value: parseInt(p.toString()),
                          label: positions.find(i => i.id == p)
                              ?.position as string,
                      };
                  })
                : [],
        });
    }, [player]);

    return (
        <section className="playerInfo" data-testid="playerInfo">
            <Form
                onSubmit={methods.handleSubmit(formHandler)}
                id="playerInfoForm"
                data-testid="playerInfoForm"
            >
                {(isAddingPlayer || player) && (
                    <PlayerForm
                        methods={methods}
                        player={player}
                        maxRosterSize={maxRosterSize}
                        isAddingPlayer={isAddingPlayer}
                        isPlayerActive={isPlayerActive}
                        isPrimaryTeam={isPrimaryTeam}
                        teamAtMaxSize={teamAtMaxSize}
                    />
                )}
                {canEditTeam(currentTeam) && (isAddingPlayer || player) && (
                    <PlayerButtons
                        isAddingPlayer={isAddingPlayer}
                        teamAtMaxSize={teamAtMaxSize}
                        setShowDeleteModal={setShowDeleteModal}
                    />
                )}
            </Form>
            {showDeleteModal && (
                <DeletePlayerModal
                    player={player}
                    showDeleteModal={showDeleteModal}
                    setShowDeleteModal={setShowDeleteModal}
                />
            )}
        </section>
    );
};

export default Player;
